import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { GraduationCap, Plus, LogOut, User as UserIcon, LayoutDashboard, Sparkles, Shield, UserPlus, Menu } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import MessagesNavButton from "@/components/MessagesNavButton";
import InviteColleagueDialog from "@/components/InviteColleagueDialog";

const ADMIN_USERNAMES = new Set(["jah1433"]);

const navLinkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${isActive ? "text-teal" : "text-navy hover:text-teal"}`;

// Larger touch targets for the mobile drawer
const mobileLinkClass = ({ isActive }) =>
  `block px-2 py-3 text-base font-medium border-l-2 transition-colors ${isActive ? "border-teal text-teal bg-teal-50" : "border-transparent text-navy hover:text-teal hover:border-teal/40"}`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) { setEligibility(null); return; }
    api.get("/me/lecture-eligibility")
      .then(({ data }) => setEligibility(data))
      .catch(() => setEligibility({ can_create: true }));
  }, [user]);

  // Poll the /lectures?upcoming=true&live=true endpoint every 60s to drive
  // the green-dot indicator on the "Upcoming" nav link. Available to both
  // anonymous visitors and logged-in users.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/lectures", { params: { upcoming: true, live: true } });
        if (!cancelled) setLiveCount(Array.isArray(data) ? data.length : 0);
      } catch { if (!cancelled) setLiveCount(0); }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const newLectureDisabled = eligibility && !eligibility.can_create;
  const availableDate = eligibility?.available_at
    ? new Date(eligibility.available_at).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const daysUntilAvailable = eligibility?.available_at
    ? Math.max(0, Math.ceil((new Date(eligibility.available_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <header className="sticky top-0 z-40 et-glass" data-testid="navbar">
      <div className="et-section flex items-center justify-between h-20 sm:h-24">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img src="https://customer-assets.emergentagent.com/job_lecture-hub-30/artifacts/cc5p8tal_Logo1.png" alt="Talkademia" className="h-16 sm:h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 text-sm">
          {user && <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">Dashboard</NavLink>}
          <NavLink to="/campus" className={navLinkClass} data-testid="nav-campus">Campus</NavLink>
          {user && <NavLink to="/schedule" className={navLinkClass} data-testid="nav-schedule">Schedule</NavLink>}
          <NavLink to="/converse" className={navLinkClass} data-testid="nav-converse">Converse</NavLink>
          <NavLink to="/upcoming" className={navLinkClass} data-testid="nav-upcoming">
            <span className="relative inline-flex items-center">
              Upcoming
              {liveCount > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600"
                  title={`${liveCount} lecture${liveCount === 1 ? "" : "s"} live right now`}
                  data-testid="nav-upcoming-live-dot"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"/>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"/>
                  </span>
                  LIVE
                </span>
              )}
            </span>
          </NavLink>
          <NavLink to="/recorded" className={navLinkClass} data-testid="nav-recorded">Archive</NavLink>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">Contact</NavLink>
          {user && (
            <button
              onClick={() => setInviteOpen(true)}
              className="text-sm font-medium text-navy hover:text-teal transition-colors inline-flex items-center gap-1"
              data-testid="nav-invite"
            >
              <UserPlus className="h-4 w-4"/>Invite
            </button>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* Mobile hamburger — visible below lg */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden p-2 rounded-md text-navy hover:text-teal hover:bg-slate-100"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="h-6 w-6"/>
                {liveCount > 0 && (
                  <span className="absolute h-2 w-2 rounded-full bg-emerald-500 animate-pulse ml-4 -mt-5"/>
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0" data-testid="mobile-menu-drawer">
              <div className="px-5 pt-5 pb-3 border-b border-slate-100">
                <img src="https://customer-assets.emergentagent.com/job_lecture-hub-30/artifacts/cc5p8tal_Logo1.png" alt="Talkademia" className="h-10 w-auto object-contain"/>
              </div>
              <nav className="px-3 py-3 space-y-0.5" data-testid="mobile-nav-links">
                {user && (
                  <NavLink onClick={() => setMobileOpen(false)} to="/dashboard" className={mobileLinkClass} data-testid="mobile-nav-dashboard">Dashboard</NavLink>
                )}
                <NavLink onClick={() => setMobileOpen(false)} to="/campus" className={mobileLinkClass} data-testid="mobile-nav-campus">Campus</NavLink>
                {user && <NavLink onClick={() => setMobileOpen(false)} to="/schedule" className={mobileLinkClass} data-testid="mobile-nav-schedule">Schedule</NavLink>}
                <NavLink onClick={() => setMobileOpen(false)} to="/converse" className={mobileLinkClass} data-testid="mobile-nav-converse">Converse</NavLink>
                <NavLink onClick={() => setMobileOpen(false)} to="/upcoming" className={mobileLinkClass} data-testid="mobile-nav-upcoming">
                  <span className="inline-flex items-center gap-2">
                    Upcoming
                    {liveCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600" data-testid="mobile-nav-upcoming-live-dot">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"/>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"/>
                        </span>
                        LIVE
                      </span>
                    )}
                  </span>
                </NavLink>
                <NavLink onClick={() => setMobileOpen(false)} to="/recorded" className={mobileLinkClass} data-testid="mobile-nav-recorded">Archive</NavLink>
                <NavLink onClick={() => setMobileOpen(false)} to="/contact" className={mobileLinkClass} data-testid="mobile-nav-contact">Contact</NavLink>
                {user && (
                  <button
                    onClick={() => { setInviteOpen(true); setMobileOpen(false); }}
                    className="block w-full text-left px-2 py-3 text-base font-medium border-l-2 border-transparent text-navy hover:text-teal hover:border-teal/40"
                    data-testid="mobile-nav-invite"
                  >
                    Invite a colleague
                  </button>
                )}
              </nav>
              {user ? (
                <div className="px-5 py-4 border-t border-slate-100 space-y-2">
                  {!newLectureDisabled && (
                    <Button
                      onClick={() => { setMobileOpen(false); navigate("/create"); }}
                      className="w-full bg-teal text-white hover:bg-teal-700"
                      data-testid="mobile-create-btn"
                    >
                      <Plus className="h-4 w-4 mr-1.5"/> New Lecture
                    </Button>
                  )}
                  {newLectureDisabled && (
                    <Link
                      to="/profile"
                      onClick={() => setMobileOpen(false)}
                      className="block text-center text-sm font-semibold text-teal hover:underline"
                      data-testid="mobile-cooldown-upgrade"
                    >
                      Next lecture available{availableDate ? ` ${availableDate}` : ""} · Upgrade →
                    </Link>
                  )}
                </div>
              ) : (
                <div className="px-5 py-4 border-t border-slate-100 space-y-2">
                  <Button
                    onClick={() => { setMobileOpen(false); navigate("/login"); }}
                    variant="outline"
                    className="w-full"
                    data-testid="mobile-login-btn"
                  >Login</Button>
                  <Button
                    onClick={() => { setMobileOpen(false); navigate("/register"); }}
                    className="w-full bg-teal text-white hover:bg-teal-700"
                    data-testid="mobile-register-btn"
                  >
                    <GraduationCap className="h-4 w-4 mr-1.5"/> Join Talkademia
                  </Button>
                </div>
              )}
            </SheetContent>
          </Sheet>

          {user ? (
            <>
              {newLectureDisabled ? (
                <div className="hidden sm:flex items-center gap-2" data-testid="navbar-cooldown-wrap">
                  <Button
                    disabled
                    className="bg-slate-200 text-slate-500 cursor-not-allowed"
                    title={availableDate ? `Available on ${availableDate}` : "Unavailable"}
                    data-testid="navbar-create-btn-disabled"
                  >
                    <Plus className="h-4 w-4 mr-1.5" /> New Lecture
                    {daysUntilAvailable > 0 && (
                      <span className="ml-1.5 text-[10px] font-bold tracking-wide">· {daysUntilAvailable}d</span>
                    )}
                  </Button>
                  <Link
                    to="/profile"
                    className="text-xs font-semibold text-teal hover:underline whitespace-nowrap"
                    data-testid="navbar-cooldown-upgrade"
                  >
                    Upgrade →
                  </Link>
                </div>
              ) : (
                <Button onClick={() => navigate("/create")} className="hidden sm:inline-flex bg-teal text-white hover:bg-teal-700" data-testid="navbar-create-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> New Lecture
                </Button>
              )}
              <NotificationBell />
              <MessagesNavButton />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-9 w-9 rounded-full bg-navy text-white text-sm font-semibold flex items-center justify-center" data-testid="user-menu-trigger">
                    {user.display_name?.[0]?.toUpperCase() || "U"}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <div className="text-sm font-semibold text-navy">{user.display_name}</div>
                    <div className="text-xs text-slate-500">@{user.username} · {user.tier === "premium" ? "Premium" : "Free"}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2"/>Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><UserIcon className="h-4 w-4 mr-2"/>Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setInviteOpen(true)} data-testid="menu-invite"><UserPlus className="h-4 w-4 mr-2"/>Invite a colleague</DropdownMenuItem>
                  {ADMIN_USERNAMES.has(user.username) && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2"/>Moderation panel</DropdownMenuItem>
                  )}
                  {user.tier !== "premium" && (
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-upgrade"><Sparkles className="h-4 w-4 mr-2"/>Upgrade to Premium</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2"/>Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <InviteColleagueDialog open={inviteOpen} onOpenChange={setInviteOpen}/>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="navbar-login-btn">Login</Button>
              <Button onClick={() => navigate("/register")} className="bg-teal text-white hover:bg-teal-700" data-testid="navbar-register-btn">
                <GraduationCap className="h-4 w-4 mr-1.5"/> Join Talkademia
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
